<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="modelValue" class="modal-overlay" @click.self="close" role="dialog" aria-modal="true" aria-labelledby="modal-title">

        <div class="modal">
          <button class="modal__close" @click="close" aria-label="Fermer">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>

          <div v-if="!sent">
            <h2 id="modal-title" class="modal__title">Être rappelé<br><span class="modal__title--accent">rapidement</span></h2>
            <p class="modal__desc">Laissez votre numéro, nous vous rappelons sous 24h.</p>

            <form class="modal__form" @submit.prevent="submit">
              <div class="modal__field">
                <label class="modal__label" for="cb-phone">VOTRE NUMÉRO</label>
                <input
                  id="cb-phone"
                  class="modal__input"
                  :class="{ 'modal__input--error': phoneError }"
                  type="tel"
                  placeholder="06 XX XX XX XX"
                  required
                  autocomplete="tel"
                  v-model="phone"
                  @input="phoneError = false"
                />
              <span v-if="phoneError" class="modal__error">Numéro de téléphone invalide</span>
              </div>
              <p v-if="sendError" class="modal__error modal__error--send">
                Une erreur est survenue. Veuillez réessayer.
              </p>
              <button type="submit" class="modal__submit" :disabled="sending">
                {{ sending ? 'Envoi en cours…' : 'Demander un rappel →' }}
              </button>
            </form>
          </div>

          <div v-else class="modal__success">
            <div class="modal__success-icon" aria-hidden="true">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <h2 class="modal__success-title">Demande envoyée !</h2>
            <p class="modal__success-desc">Notre équipe vous recontactera rapidement au <strong>{{ phone }}</strong>.</p>
            <button class="modal__submit" @click="close">Fermer</button>
          </div>
        </div>

      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  modelValue: Boolean,
})
const emit = defineEmits(['update:modelValue'])

const phone = ref('')
const sent = ref(false)
const sending = ref(false)
const phoneError = ref(false)
const sendError = ref(false)

function isValidFrenchPhone(value) {
  const cleaned = value.replace(/[\s.\-()]/g, '')
  return /^(?:(?:\+|00)33|0)[1-9](?:\d{8})$/.test(cleaned)
}

function close() {
  emit('update:modelValue', false)
}

async function submit() {
  if (!isValidFrenchPhone(phone.value)) {
    phoneError.value = true
    return
  }

  sending.value = true
  sendError.value = false

  try {
    const res = await fetch('/api/callback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone: phone.value }),
    })
    if (!res.ok) throw new Error('send failed')
    sent.value = true
  } catch {
    sendError.value = true
  } finally {
    sending.value = false
  }
}

watch(() => props.modelValue, (val) => {
  if (val) {
    phone.value = ''
    sent.value = false
    sending.value = false
    phoneError.value = false
    sendError.value = false
  }
})

function onKeydown(e) {
  if (e.key === 'Escape') close()
}
onMounted(() => window.addEventListener('keydown', onKeydown))
onUnmounted(() => window.removeEventListener('keydown', onKeydown))
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal {
  background-color: var(--color-white);
  border-radius: 2px;
  padding: 2.5rem;
  width: 100%;
  max-width: 440px;
  position: relative;
  border-top: 4px solid var(--color-accent);
}

.modal__close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-text-muted);
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}
.modal__close:hover { color: var(--color-heading); }

.modal__badge {
  display: inline-block;
  background-color: #6bd1f8;
  color: #005970;
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: var(--text-badge);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wider);
  padding: 4px 12px;
  border-radius: 2px;
  margin-bottom: 1rem;
}

.modal__title {
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: clamp(1.75rem, 4vw, 2.25rem);
  text-transform: uppercase;
  letter-spacing: -0.05em;
  color: var(--color-heading);
  line-height: 1.05;
  margin: 0 0 0.75rem;
}

.modal__title--accent { color: var(--color-accent); }

.modal__desc {
  font-family: var(--font-body);
  font-size: var(--text-sm);
  color: var(--color-text-muted);
  margin: 0 0 1.75rem;
  line-height: 1.5;
}

.modal__form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.modal__field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.modal__label {
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: var(--color-text);
}

.modal__input {
  background-color: #f3f3f3;
  border: none;
  padding: 1rem;
  border-radius: 2px;
  font-family: var(--font-body);
  font-size: 1rem;
  color: var(--color-heading);
  width: 100%;
  box-sizing: border-box;
  outline: 2px solid transparent;
  transition: outline-color 0.15s;
}
.modal__input:focus { outline: 2px solid var(--color-accent); }
.modal__input--error { outline: 2px solid #e53e3e; }

.modal__error {
  font-family: var(--font-body);
  font-size: 0.75rem;
  color: #e53e3e;
  margin-top: 0.25rem;
}

.modal__submit {
  background-color: var(--color-accent);
  color: var(--color-white);
  width: 100%;
  padding: 1rem;
  border: none;
  border-radius: 2px;
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: var(--text-sm);
  text-transform: uppercase;
  letter-spacing: 1.4px;
  cursor: pointer;
  transition: opacity 0.2s;
}
.modal__submit:hover { opacity: 0.9; }
.modal__submit:disabled { opacity: 0.6; cursor: not-allowed; }

.modal__error--send {
  font-family: var(--font-body);
  font-size: 0.75rem;
  color: #e53e3e;
  margin: 0;
}

/* Success state */
.modal__success {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1rem;
  padding-block: 1rem;
}

.modal__success-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: #e6f4ea;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1a7a34;
}

.modal__success-title {
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: 1.5rem;
  text-transform: uppercase;
  letter-spacing: -0.03em;
  color: var(--color-heading);
  margin: 0;
}

.modal__success-desc {
  font-family: var(--font-body);
  font-size: var(--text-sm);
  color: var(--color-text-muted);
  line-height: 1.6;
  margin: 0;
}

/* Transition */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}
.modal-enter-active .modal,
.modal-leave-active .modal {
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
.modal-enter-from .modal,
.modal-leave-to .modal {
  transform: translateY(12px);
  opacity: 0;
}
</style>
